import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api-client";
import type { ExportSuccessRow } from "../components/ExportSuccessChart";

export type ExportSuccessTier = "hot" | "cold";
export type ExportSuccessBucket = "hour" | "day" | "week" | "month";

export interface ExportSuccessData {
  tier: ExportSuccessTier;
  bucket: ExportSuccessBucket;
  rows: ExportSuccessRow[];
}

export function useExportSuccess(
  from?: string,
  to?: string,
  bucket?: ExportSuccessBucket,
) {
  const [data, setData] = useState<ExportSuccessData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (from) params.set("from", from);
      if (to) params.set("to", to);
      if (bucket) params.set("bucket", bucket);
      const query = params.toString() ? `?${params.toString()}` : "";
      const result = await api.get<ExportSuccessData>(`/analytics/export-success${query}`);
      setData(result);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load export success data");
    } finally {
      setIsLoading(false);
    }
  }, [from, to, bucket]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, isLoading, error, refetch: fetchData };
}
